import { Box, IconButton, SvgIcon, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Chip, Popover, Typography, useTheme } from '@mui/material';
import { usePosMappingsContext } from '../../context/PosMappingsContext';
import { Trash01 } from '../../../../assets';
import { UpdateStoreName } from '../../../../app/services/api/generated';
import { ItemType } from '../../enums/ItemType';
import React from 'react';
import { EmptyTable } from '../../../../components/shared/EmptyTable';
import { TableHeader } from '../../../../components/forms/TableHeader';

const getItemTypeLabel = (mappingType?: number): string => {
  switch (mappingType) {
    case ItemType.StandaloneItem:
      return 'Standalone Item';
    case ItemType.Extra:
      return 'Extra';
    case ItemType.Transition:
      return 'Transition';
    case ItemType.Substitution:
      return 'Substitution';
    case ItemType.Swap:
      return 'Swap';
    case ItemType.Removal:
      return 'Removal';
    default:
      return '';
  }
};

export function PosSkuTable() {
  const {
    posMappings,
    handleSort,
    order,
    orderBy,
    t,
    posMappingDelete,
    edit,
    totalCount,
    clearAll,
    skuSearch,
    openCreateModal,
  } = usePosMappingsContext();

  const theme = useTheme();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [popoverStores, setPopoverStores] = React.useState<UpdateStoreName[]>([]);

  const handleOpenPopover = (event: React.MouseEvent<HTMLElement>, extraStores: UpdateStoreName[]) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setPopoverStores(extraStores);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
    setPopoverStores([]);
  };

  const isPopoverOpen = Boolean(anchorEl);

  return (
    <>
      {totalCount === 0 ? (
        <EmptyTable
          text={t('posMapping')}
          searchTerm={skuSearch}
          onClear={clearAll}
          onAdd={openCreateModal}
          onAddIsCreating={false}
          onAddIsDisabled={false}
          addItemText={t('createNew')}
        />
      ) : (
        <>
          <TableHeader>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <span>{t('posMapping')}</span>
            </Box>
          </TableHeader>
          <TableContainer component="div">
            <Table size="small"
              aria-label="POS SKU table">
              <TableHead>

                <TableRow>
                  <TableCell align="left"
                    sx={{ width: '10%' }}>
                    <TableSortLabel
                      active={orderBy === 'source'}
                      direction={order}
                      onClick={() => handleSort('source')}
                    >
                      {t('posSku')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="left"
                    sx={{ width: '10%' }}>
                    <TableSortLabel
                      active={orderBy === 'target'}
                      direction={order}
                      onClick={() => handleSort('target')}
                    >
                      {t('imSku')}
                    </TableSortLabel>
                  </TableCell>

                  <TableCell align="left">
                    <TableSortLabel
                      active={orderBy === 'itemDesc'}
                      direction={order}
                      onClick={() => handleSort('itemDesc')}
                    >
                      {t('itemDesc')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="left">
                    <TableSortLabel
                      active={orderBy === 'mappingType'}
                      direction={order}
                      onClick={() => handleSort('mappingType')}
                    >
                      {t('itemType')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="left">
                    {t('stores')}
                  </TableCell>
                  <TableCell align="left"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {posMappings?.map((row) => (
                  <TableRow
                    key={`${row.source}-${row.target}-${row.mappingType}`}
                    hover
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: '#d1f7e2',
                        '& td': {
                          color: '#007f5f',
                          fontWeight: 500,
                        },
                      },
                    }}
                    onClick={() => edit(row)}
                  >
                    <TableCell sx={{ width: '20%' }}>{row.source}</TableCell>
                    <TableCell sx={{ width: '20%' }}>{row.target}</TableCell>
                    <TableCell>{row.itemDesc}</TableCell>
                    <TableCell>{getItemTypeLabel(row.mappingType)}</TableCell>
                    <TableCell>
                      {Array.isArray(row.stores) && (row.stores?.length ?? 0) > 0 ? (
                        <Box display="flex"
                          flexDirection="column"
                          alignItems="flex-start"
                          gap={0.5}>
                          <Box display="flex"
                            flexWrap="wrap"
                            gap={1}>
                            {row.stores.slice(0, 3).map((store: UpdateStoreName) => (
                              <Chip
                                key={store.storeNumber}
                                label={store.storeName ?? ''}
                                size="small"
                                sx={{ m: 0.5 }}
                              />
                            ))}
                          </Box>
                          {row.stores && row.stores.length > 3 && (
                            <Chip
                              label={`+${row.stores.length - 3}`}
                              size="small"
                              clickable
                              onClick={(e) => handleOpenPopover(e, row.stores?.slice(3) || [])}
                              sx={{
                                backgroundColor: theme.palette.custom.gray[200],
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: theme.palette.custom.gray[300] },
                              }}
                            />
                          )}
                        </Box>
                      ) : (
                        <Chip label={t('allStores')}
                          size="small"
                          color="default" />
                      )}
                    </TableCell>
                    <TableCell align="left">
                      <Box display="flex"
                        gap={1}>
                        <Box
                          onClick={(e) => e.stopPropagation()}
                          sx={{ '&:hover': { backgroundColor: 'transparent !important' } }}
                        >
                          <IconButton size="small"
                            aria-label="delete"
                            onClick={() => posMappingDelete(row)}>
                            <SvgIcon style={{ color: theme.palette.error[500] }}>
                              <Trash01 />
                            </SvgIcon>
                          </IconButton>
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Popover
            open={isPopoverOpen}
            anchorEl={anchorEl}
            onClose={handleClosePopover}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <Box
              display="flex"
              gap={1}
              flexWrap="wrap"
              maxWidth={500}
              minWidth={300}
              p={2}
            >
              {popoverStores.map((store: UpdateStoreName) => (
                <Chip
                  key={store.storeNumber}
                  label={store.storeName ?? ''}
                  size="small"
                  sx={{ m: 0.5 }}
                />
              ))}
            </Box>
          </Popover>
        </>
      )}
    </>
  );
}
